import { Box } from '@mui/material';
import React, { FC, useEffect, useRef } from 'react';

import { LocationSuggestionGroup } from '@abg/typings/biz/location.interface';

import { GroupBackButton } from '../../atoms/GroupBackButton';
import { GroupShowButton } from '../../atoms/GroupShowButton';
import { GroupTitle } from '../../atoms/GroupTitle';

type Properties = {
  group: LocationSuggestionGroup;
  groupPreviewLimit: number;
  groupItems: React.ReactNode;
  isGroupSelected: boolean;
  requestedInputValue: string;
  labels: {
    groupTitle: string;
    showAll: string;
    showingAll: string;
    back: string;
  };
  onBack: () => void;
  onSelect: (group: LocationSuggestionGroup) => void;
};

const SuggestionGroupComponent: FC<Properties> = ({
  group,
  groupPreviewLimit,
  groupItems,
  isGroupSelected,
  requestedInputValue,
  labels,
  onBack,
  onSelect,
}) => {
  const groupCount = React.Children.count(groupItems);
  const showingAllListReference = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    if (!isGroupSelected) return;

    const listElement = showingAllListReference.current;
    if (!listElement) return;

    requestAnimationFrame(() => {
      const inputElement = document.querySelector<HTMLInputElement>(
        'input[role="combobox"][aria-expanded="true"]',
      );
      const firstOptionElement =
        listElement.querySelector<HTMLLIElement>('li[role="option"]');
      if (!inputElement || !firstOptionElement) return;

      const hasHighlightInList = Boolean(
        listElement.querySelector<HTMLLIElement>(
          'li[role="option"].Mui-focused, li[role="option"].Mui-focusVisible',
        ),
      );

      if (!hasHighlightInList) {
        const event = new KeyboardEvent('keydown', {
          key: 'ArrowDown',
          bubbles: true,
        });
        inputElement.dispatchEvent(event);
      }

      inputElement.focus();
    });
  }, [isGroupSelected, groupCount]);

  return isGroupSelected ? (
    <li>
      <GroupBackButton
        data-testid="location-search-back-button"
        label={labels.back ?? ''}
        onClick={onBack}
      />
      <GroupTitle
        dataTestId="location-search-showing-all-title"
        title={labels.showingAll
          .replace('{{amount}}', groupCount.toString())
          .replace('{{inputValue}}', requestedInputValue || '')}
      />
      <Box
        component="ul"
        ref={showingAllListReference}
        sx={{ margin: 0, padding: 0 }}
      >
        {groupItems}
      </Box>
    </li>
  ) : (
    <li>
      <GroupTitle title={labels.groupTitle} />
      <Box component="ul" sx={{ margin: 0, padding: 0 }}>
        {React.Children.toArray(groupItems).slice(0, groupPreviewLimit)}
      </Box>
      {groupCount > groupPreviewLimit && (
        <GroupShowButton
          data-testid="location-search-show-all-button"
          label={labels.showAll.replace('{{amount}}', groupCount.toString())}
          onClick={() => onSelect(group)}
        />
      )}
    </li>
  );
};

export const SuggestionGroup = React.memo(SuggestionGroupComponent);
